import { Fragment } from "react";
import type { ComponentProps, PropsWithChildren } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Portal } from "radix-ui";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

import { IsoLogo } from "@/assets";

const logoLoaderVariants = tv({
  slots: {
    overlay:
      "pointer-events-auto inset-0 z-50 size-full cursor-wait bg-neutral-200/60 backdrop-blur-[2px]",
    content:
      "flex size-full flex-col items-center justify-center p-4 text-center text-xl font-medium text-neutral-800 sm:mx-auto sm:max-w-2xl sm:text-2xl",
    logo: "size-24 -translate-y-1/5 animate-loading-icon sm:size-28",
  },
  variants: {
    variant: {
      secondary: {
        overlay: "bg-primary-50 backdrop-blur-none",
        content: "text-white",
      },
    },
    fullscreen: {
      true: { overlay: "fixed" },
      false: { overlay: "absolute" },
    },
  },
});

const { overlay, content, logo } = logoLoaderVariants();

interface LogoLoaderProps
  extends PropsWithChildren,
    VariantProps<typeof logoLoaderVariants> {
  show?: boolean;
  fullscreen?: boolean;
  withOverlay?: boolean;
}

const Wrapper = ({
  className,
  animated,
  children,
  fullscreen,
}: ComponentProps<"div"> & {
  animated: boolean;
  fullscreen: boolean;
}) => {
  const PortalWrapper = fullscreen ? Portal.Root : Fragment;

  return (
    <PortalWrapper>
      {animated ? (
        <motion.div
          className={className}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {children}
        </motion.div>
      ) : (
        children
      )}
    </PortalWrapper>
  );
};

export const LogoLoader = ({
  show = true,
  children,
  variant,
  fullscreen = false,
  withOverlay = false,
}: LogoLoaderProps) => {
  return (
    <AnimatePresence>
      {show && (
        <Wrapper
          fullscreen={fullscreen}
          animated={fullscreen || withOverlay}
          className={overlay({ variant, fullscreen })}
        >
          <div className={content({ variant })}>
            <IsoLogo className={logo()} />
            {children}
          </div>
        </Wrapper>
      )}
    </AnimatePresence>
  );
};
